import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { DEFAULT_WINDOW, REQUEST_WARNING_MESSAGE, SCHEDULE_HORIZON_DAYS, addDays, dayLabel, requestDate, requestWarning } from '@kronos/domain'
import type { DayAvailability, LocalDate } from '@kronos/domain'
import { repository } from '../../data'
import type { TeamSchedule } from '../../data/schedule'
import { problemOf } from '../hours/time'
import { Modal } from '../kit'
import { DayRequests } from './PendingList'
import { availabilityText, useScheduleChanged } from './shared'

/** Who and which day a request form opens on. */
export interface Ask {
  readonly technicianId: string
  readonly date: LocalDate
}

/**
 * Ask a technician to come in. Opens on the person and day that were clicked,
 * with the hours they said they can do; says so, plainly, if the day is one
 * they marked off — and still lets you send it.
 */
export function RequestForm({ team, ask, onClose }: { team: TeamSchedule; ask: Ask; onClose: () => void }): JSX.Element {
  const changed = useScheduleChanged()
  const availabilityOf = (technicianId: string, date: string): DayAvailability | null =>
    team.availability.find((a) => a.userId === technicianId && a.date === date) ?? null
  const windowFor = (technicianId: string, date: string): { from: string; to: string } =>
    availabilityOf(technicianId, date)?.window ?? DEFAULT_WINDOW

  const [technicianId, setTechnicianId] = useState(ask.technicianId)
  const [date, setDate] = useState<string>(ask.date)
  const [from, setFrom] = useState<string>(windowFor(ask.technicianId, ask.date).from)
  const [to, setTo] = useState<string>(windowFor(ask.technicianId, ask.date).to)
  const [note, setNote] = useState('')
  const [problem, setProblem] = useState('')

  // Another person or day: offer *their* hours for it.
  const pick = (who: string, day: string): void => {
    setTechnicianId(who)
    setDate(day)
    const w = windowFor(who, day)
    setFrom(w.from)
    setTo(w.to)
  }

  const send = useMutation({
    mutationFn: () => repository.requestComeIn({ technicianId, date, from, to, note }),
    onSuccess: () => {
      changed()
      onClose()
    },
    onError: (e) => setProblem(problemOf(e)),
  })

  const name = team.technicians.find((t) => t.id === technicianId)?.name ?? 'them'
  const last = addDays(team.from, team.days - 1)
  // Only judged inside the fortnight loaded; outside it the form says nothing rather than guess.
  const known = date >= team.from && date <= last
  const availability = known ? availabilityOf(technicianId, date) : null
  const warning = known ? requestWarning(availability, from, to) : null
  const sameDay = team.requests.filter((r) => r.technicianId === technicianId && requestDate(r, team.timeZone) === date)

  return (
    <Modal
      title={`Ask ${name} to come in`}
      subtitle={/^\d{4}-\d{2}-\d{2}$/.test(date) ? dayLabel(date as LocalDate) : undefined}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={send.isPending} onClick={() => send.mutate()}>
            Send request
          </button>
        </>
      }
    >
      <div className="stack schedule-form">
        <div className="sched-form__grid">
          <label className="field">
            <span className="field-label">Technician</span>
            <select value={technicianId} onChange={(e) => pick(e.target.value, date)}>
              {team.technicians.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Day</span>
            <input
              type="date"
              value={date}
              min={team.today}
              max={addDays(team.today, SCHEDULE_HORIZON_DAYS)}
              onChange={(e) => pick(technicianId, e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">From</span>
            <input type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">To</span>
            <input type="time" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span className="field-label">What for</span>
          <input maxLength={140} placeholder="e.g. Assemble arms" value={note} onChange={(e) => setNote(e.target.value)} />
        </label>

        {known ? (
          <p className={`sched-form__said${warning === 'unavailable' ? ' warn' : ''}`}>
            {warning ? REQUEST_WARNING_MESSAGE[warning] : `They said they can do ${availabilityText(availability)}.`}
            {availability?.note ? <span className="muted"> “{availability.note}”</span> : null}
          </p>
        ) : null}
        {sameDay.length > 0 ? <DayRequests requests={sameDay} timeZone={team.timeZone} now={team.now} /> : null}
        {problem ? (
          <p className="critical" role="alert">
            {problem}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
